import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";

import { filterExpertBasedOnAgendaResponsesOptions, getAgendaData, getFilteredProjectAgendaData } from "./helper";
import { style1, style2 } from "./style";
import ViewAgendaSkeleton from "../../../atoms/skeletons/project-details/viewAgenda";
import CustomBtnFilled from "../../../atoms/form-molecules/CustomBtnFilled";
import { AgendaDataState, AgendaResponse } from "../../../pages/Projects/types";
import { isSuperAdmin } from "../../../utils/role";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useProjectPageContext } from "../../../pages/Projects/helper";
import TooltipIcons from "../../../atoms/project-card-footer-icons";
import { useSnackbar } from "notistack";
import { handleCopy } from "../../../molecules/expert-profile-sections/profile-section/helper";
import DropDownFilter from "../../../atoms/drop-down-filter";

interface ViewAgendaProps {
  open: boolean;
  onClose: () => void;
  openAddAgenda: () => void;
  projectId: string | null;
  openAgendaDescription: (
    fk_agenda: number | null,
    agenda_responses: AgendaResponse[] | null,
    pe_id: number,
    isAdminAllowed: boolean,
    isProjectOpen: boolean
  ) => void;
  openShareAgendaPreview: (
    fk_agenda: number | null,
    pe_id: number,
    projectId: string | null,
    isAgendaIdChanged: boolean,
  ) => void;
  isAgendaIdChanged: boolean;
  isAdminAllowed: boolean;
  isProjectOpen: boolean;
}

const Agenda: React.FC<ViewAgendaProps> = ({
  open,
  onClose,
  openAddAgenda,
  projectId,
  openAgendaDescription,
  openShareAgendaPreview,
  isAgendaIdChanged,
  isAdminAllowed,
  isProjectOpen
}) => {
  const [agendaResponseFilter, setAgendaResponseFilter] = useState('default');
  const [filteredProjectAgendaData, setFilteredProjectAgendaData] = useState<AgendaDataState | null>(null);
  const { projectAgendaData, setProjectAgendaData } = useProjectPageContext()
  useEffect(() => {
    open && projectId && getAgendaData(setProjectAgendaData, projectId);
  }, [open, isAgendaIdChanged]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (projectAgendaData) {
      const filteredData = getFilteredProjectAgendaData(projectAgendaData, agendaResponseFilter);
      setFilteredProjectAgendaData(filteredData);
    }
  }, [agendaResponseFilter, projectAgendaData]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "50px",
      }}
    >
      <Box
        style={{
          width: 700,
          maxHeight: 600,
          backgroundColor: "white",
          borderRadius: "10px",
        }}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          padding={"12px 20px"}
        >
          <Typography
            fontSize={"16px"}
            fontWeight={"600"}
            sx={{ opacity: "0.80" }}
          >
            Agenda
          </Typography>
          <CloseIcon
            sx={{
              fontSize: "18px",
              color: "#434343",
              opacity: "0.75",
              cursor: "pointer",
            }}
            onClick={onClose}
          />
        </Stack>
        <hr style={{ opacity: "0.75" }} />
        <Grid
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          padding={"20px 0"}
          container
        >
          <CustomBtnFilled
            variant="Contained"
            label={(filteredProjectAgendaData?.applicable_agenda_id || !isProjectOpen) ? "View Agenda" : "Add Agenda"}
            onClick={openAddAgenda}
            styles={{
              width: "70%",
              height: "2.5rem",
              borderRadius: "30px",
            }}
          />
        </Grid>
        {filteredProjectAgendaData?.agenda?.length === 0 ?
          <Typography
            fontSize={"14px"}
            fontWeight={"600"}
            sx={{ opacity: "0.80", textAlign: "center" }}
            width={"100%"}
            pb={"10px"}
          >
            No Agenda Shared
          </Typography> :
          <>
            <Box display={"flex"} alignItems={"center"} justifyContent={'space-between'} sx={{ padding: "0 1rem 1rem" }}>
              <Typography
                fontSize={"14px"}
                fontWeight={"600"}
                style={{ opacity: "0.80" }}
              >
                Agenda Shared with
              </Typography>
              <DropDownFilter
                filterValue={agendaResponseFilter}
                setFilterPayload={setAgendaResponseFilter}
                dropDownItems={filterExpertBasedOnAgendaResponsesOptions}
              />
            </Box>
            <Grid container
              mb={"1rem"}
              sx={{ overflowY: "scroll", maxHeight: "330px", borderTop: "1px solid rgba(0,0,0,0.3)", borderBottom: "1px solid rgba(0,0,0,0.3)" }}
              paddingTop={"0.25rem"}
            >
              {filteredProjectAgendaData?.agenda ? (
                filteredProjectAgendaData?.agenda?.map((data: any) => (
                  <Grid
                    key={data.id}
                    container
                    item
                    padding={"0 20px 5px"}
                    alignItems={"center"}
                    justifyContent={'space-between'}
                  >
                    <Grid item xs={4.6}>
                      <Typography
                        fontSize={"13px"}
                        fontWeight={"600"}
                        style={{ opacity: "0.75" }}
                      >
                        {data.expert_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.6}>
                      <Chip
                        sx={data.agenda_responses ? style1 : style2}
                        label={data.agenda_responses ? "Completed" : "Pending"}
                      />
                    </Grid>
                    <Grid item xs={4.5} display="flex" justifyContent="flex-end" alignItems="center" gap={1.5}>
                      {(isSuperAdmin() || data.agenda_responses || isAdminAllowed) && (
                        <>
                          <CustomBtnFilled
                            label={data.agenda_responses ? "View" : "Add"}
                            variant="contained"
                            onClick={() => {
                              openAgendaDescription(
                                data.fk_agenda,
                                data.agenda_responses,
                                data.id,
                                isAdminAllowed,
                                isProjectOpen
                              );
                            }}
                          />
                        </>
                      )}
                      {isSuperAdmin() && (
                        <>
                          <CustomBtnFilled
                            label={data?.agenda_status ? 'Reshare' : 'Share'}
                            variant="contained"
                            styles={{
                              whiteSpace: "nowrap",
                              minWidth: "auto",
                            }}
                            onClick={() => {
                              openShareAgendaPreview(
                                data.fk_agenda,
                                data.id,
                                projectId,
                                isAgendaIdChanged,
                              )
                            }}
                          />
                          {data?.meta && <TooltipIcons
                            isIcon={false}
                            isMaterialIcon={true}
                            MaterialIcon={ContentCopyIcon}
                            sxIconButton={{ width: '35px', height: '35px' }}
                            sxMaterialIcon={{
                              fontSize: '20px',
                            }}
                            title="copy agenda link"
                            isDisabled={data?.agenda_status !== 'Sent' || !data?.meta?.project_agenda_link }
                            handleClick={() => {
                              (handleCopy(
                                data?.meta?.project_agenda_link,
                                enqueueSnackbar,
                                "Agenda link",
                              ));
                            }}
                          />}

                        </>
                      )}
                    </Grid>
                  </Grid>
                ))
              ) : (
                <ViewAgendaSkeleton />
              )}
            </Grid>
          </>}
      </Box>
    </Modal>
  );
};

export default Agenda;
